/**
 * Form field primitives — text, textarea, select, boolean, number.
 *
 * Typed labels + controlled values. Each editor composes these into a
 * domain-specific form; no generic schema-to-form generator.
 */

import type { ChangeEvent, ReactNode } from 'react';

interface BaseFieldProps {
  label: string;
  required?: boolean;
  hint?: string;
  error?: string;
}

function FieldShell({
  label,
  required,
  hint,
  error,
  children,
}: BaseFieldProps & { children: ReactNode }): JSX.Element {
  return (
    <label className="form-field">
      <span className="form-field__label">
        {label}
        {required && <span className="form-field__required" aria-hidden> *</span>}
      </span>
      {children}
      {hint && !error && <span className="form-field__hint">{hint}</span>}
      {error && (
        <span className="form-field__error" role="alert">
          {error}
        </span>
      )}
    </label>
  );
}

// ─── Text ────────────────────────────────────────────────────────────────

export interface TextFieldProps extends BaseFieldProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
  monospace?: boolean;
}

export function TextField(props: TextFieldProps): JSX.Element {
  return (
    <FieldShell label={props.label} required={props.required} hint={props.hint} error={props.error}>
      <input
        type="text"
        value={props.value}
        placeholder={props.placeholder}
        maxLength={props.maxLength}
        disabled={props.disabled}
        onChange={(e: ChangeEvent<HTMLInputElement>) => props.onChange(e.target.value)}
        className={props.monospace ? 'form-input form-input--mono' : 'form-input'}
      />
    </FieldShell>
  );
}

// ─── Textarea ────────────────────────────────────────────────────────────

export interface TextAreaProps extends BaseFieldProps {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
  disabled?: boolean;
}

export function TextArea(props: TextAreaProps): JSX.Element {
  return (
    <FieldShell label={props.label} required={props.required} hint={props.hint} error={props.error}>
      <textarea
        value={props.value}
        rows={props.rows ?? 3}
        placeholder={props.placeholder}
        disabled={props.disabled}
        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => props.onChange(e.target.value)}
        className="form-input form-input--textarea"
      />
    </FieldShell>
  );
}

// ─── Select ──────────────────────────────────────────────────────────────

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectFieldProps extends BaseFieldProps {
  value: string;
  onChange: (v: string) => void;
  options: ReadonlyArray<SelectOption>;
  placeholder?: string;
  disabled?: boolean;
}

export function SelectField(props: SelectFieldProps): JSX.Element {
  return (
    <FieldShell label={props.label} required={props.required} hint={props.hint} error={props.error}>
      <select
        value={props.value}
        disabled={props.disabled}
        onChange={(e: ChangeEvent<HTMLSelectElement>) => props.onChange(e.target.value)}
        className="form-input"
      >
        {props.placeholder && <option value="">{props.placeholder}</option>}
        {props.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}

// ─── Boolean (checkbox) ──────────────────────────────────────────────────

export interface BoolFieldProps {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  hint?: string;
}

export function BoolField(props: BoolFieldProps): JSX.Element {
  return (
    <label className="form-bool">
      <input
        type="checkbox"
        checked={props.value}
        disabled={props.disabled}
        onChange={(e) => props.onChange(e.target.checked)}
      />
      <span>{props.label}</span>
      {props.hint && <span className="form-field__hint"> — {props.hint}</span>}
    </label>
  );
}

// ─── Number ──────────────────────────────────────────────────────────────

export interface NumberFieldProps extends BaseFieldProps {
  value: number | null;
  onChange: (v: number | null) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  placeholder?: string;
}

export function NumberField(props: NumberFieldProps): JSX.Element {
  return (
    <FieldShell label={props.label} required={props.required} hint={props.hint} error={props.error}>
      <input
        type="number"
        value={props.value ?? ''}
        min={props.min}
        max={props.max}
        step={props.step}
        placeholder={props.placeholder}
        disabled={props.disabled}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          const v = e.target.value.trim();
          props.onChange(v === '' ? null : Number(v));
        }}
        className="form-input"
      />
    </FieldShell>
  );
}
